import { SolutionScenario } from '@/types/scenarios/solution';
import { Cog, Phone, Calendar, Users, Wrench, AlertTriangle, Bell, ClipboardList, Truck, HardHat } from 'lucide-react';

export const industrial: SolutionScenario = {
  meta: {
    id: 'industrial',
    slug: 'industrial',
    name: 'Industrial',
    tagline: 'Genies that triage breakdowns and deflect tier one service calls',
    description: "A genie that triages breakdowns, identifies parts, and answers the same operator questions every shift so your service desk only handles the calls that actually need a human.",
    market: 'Industrial',
    subMarkets: ['Heavy Equipment', 'Industrial Supplies', 'Material Handling', 'Pumps & Valves', 'Power Generation'],
    specificNeeds: [
      '24/7 emergency support lines',
      'Parts identification & ordering',
      'Equipment service scheduling',
      'Warranty claims processing',
      'Technical specifications assistance',
      'Safety incident reporting'
    ],
    commonRoles: ['Field Service Techs', 'Parts Counter Staff', 'Service Coordinators', 'Safety Officers'],
    marketSize: '$5.2 trillion globally',
    isFeatured: true
  },

  hero: {
    headline: 'Service calls, triaged',
    headlineGradient: 'Service calls,',
    headlineRest: 'triaged',
    subheadline: "A genie that triages breakdowns, identifies parts, and answers the same operator questions every shift so your service desk only handles the calls that actually need a human.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'interactive-faq',
    'proactive-welcome'
  ],

  applications: [
    {
      id: 'emergency-support',
      name: 'Emergency Support Line',
      description: 'Critical 24/7 support for equipment failures and urgent situations',
      department: 'Service',
      icon: AlertTriangle,
      playbookIds: ['interactive-faq', 'lead-capture'],
      benefits: [
        '24/7 emergency response',
        'Immediate triage and prioritization',
        'Dispatch coordination',
        'Reduce downtime for customers'
      ]
    },
    {
      id: 'parts-ordering',
      name: 'Parts Identification & Ordering',
      description: 'Help customers identify and order the right parts quickly',
      department: 'Parts',
      icon: Cog,
      playbookIds: ['interactive-faq', 'lead-capture'],
      benefits: [
        'Part number lookup assistance',
        'Cross-reference identification',
        'Stock availability checks',
        '24/7 order placement'
      ]
    },
    {
      id: 'service-scheduling',
      name: 'Service & Maintenance Scheduling',
      description: 'Schedule preventive maintenance, repairs, and inspections',
      department: 'Service',
      icon: Calendar,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        'Online service scheduling',
        'Preventive maintenance reminders',
        'Technician dispatch coordination',
        'Reduce scheduling calls by 45%'
      ]
    },
    {
      id: 'technical-specs',
      name: 'Technical Specifications',
      description: 'Provide equipment specifications, manuals, and technical guidance',
      department: 'Technical Support',
      icon: ClipboardList,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Instant spec lookups',
        'Operating parameter guidance',
        'Installation requirements',
        'Compatibility verification'
      ]
    },
    {
      id: 'warranty-claims',
      name: 'Warranty & Claims Processing',
      description: 'Initiate and track warranty claims and returns',
      department: 'Customer Service',
      icon: Wrench,
      playbookIds: ['lead-capture', 'interactive-faq'],
      benefits: [
        'Warranty verification',
        'Claims intake automation',
        'Status tracking',
        'Faster claim resolution'
      ]
    },
    {
      id: 'safety-reporting',
      name: 'Safety Incident Reporting',
      description: 'Capture and route safety incidents and near-miss reports',
      department: 'Safety',
      icon: HardHat,
      playbookIds: ['lead-capture'],
      benefits: [
        '24/7 incident reporting',
        'Structured data capture',
        'Automatic escalation',
        'Compliance documentation'
      ]
    }
  ],

  challenges: [
    {
      title: 'Critical Equipment Downtime',
      description: 'Equipment failures can cost industrial customers thousands per hour in lost productivity',
      impact: 'high',
      affectedAreas: ['Customer operations', 'Revenue impact', 'Relationship risk']
    },
    {
      title: 'Complex Parts Identification',
      description: 'Finding the right part among thousands of SKUs requires expertise and time',
      impact: 'high',
      affectedAreas: ['Order accuracy', 'Customer satisfaction', 'Return rates']
    },
    {
      title: 'After Hours Emergency Calls',
      description: 'Industrial operations run 24/7 but support staff availability is limited',
      impact: 'high',
      affectedAreas: ['Response time', 'Customer retention', 'Service revenue']
    },
    {
      title: 'Service Coordination Complexity',
      description: 'Coordinating technicians, parts, and customer schedules is challenging',
      impact: 'medium',
      affectedAreas: ['Technician utilization', 'First-time fix rates', 'Customer experience']
    }
  ],

  solutions: [
    {
      challenge: 'Equipment downtime emergencies',
      solution: 'Deploy 24/7 emergency triage Voice Genie with intelligent routing',
      implementation: 'Configure severity assessment and automatic dispatch triggers',
      expectedResults: [
        '70% faster emergency response',
        'Proper prioritization of critical issues',
        'Reduced customer downtime'
      ]
    },
    {
      challenge: 'Parts identification bottlenecks',
      solution: 'Voice-guided parts lookup with cross-reference database',
      implementation: 'Integrate with parts catalog and inventory systems',
      expectedResults: [
        '50% reduction in parts counter calls',
        'Improved order accuracy',
        '24/7 parts ordering capability'
      ]
    },
    {
      challenge: 'Service scheduling inefficiency',
      solution: 'Automated service scheduling with technician calendar integration',
      implementation: 'Connect to dispatch system and customer equipment records',
      expectedResults: [
        '40% reduction in scheduling calls',
        'Better technician utilization',
        'Improved first-time fix rates'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'ServiceMax',
        category: 'Field Service',
        description: 'Field service management for equipment-centric industries',
        required: false,
        popularityScore: 88
      },
      {
        name: 'IFS',
        category: 'ERP',
        description: 'Enterprise software for asset-intensive industries',
        required: false,
        popularityScore: 82
      },
      {
        name: 'Infor',
        category: 'ERP',
        description: 'Industry-specific cloud applications',
        required: false,
        popularityScore: 80
      },
      {
        name: 'eMaint',
        category: 'CMMS',
        description: 'Computerized maintenance management system',
        required: false,
        popularityScore: 75
      }
    ],
    common: [
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Enterprise CRM platform',
        required: false,
        popularityScore: 95
      },
      {
        name: 'SAP',
        category: 'ERP',
        description: 'Enterprise resource planning',
        required: false,
        popularityScore: 92
      }
    ]
  },

  metrics: {
    averageImplementationTime: '2-4 weeks',
    typicalROI: '350%',
    adoptionRate: '88% customer satisfaction',
    customerSatisfaction: '4.6/5'
  },

  demoConfig: {
    firstMessage: "Hello! Thanks for calling. I can help you with emergency support, parts orders, service scheduling, or technical questions. Is this an emergency situation?",
    systemPrompt: "You are a knowledgeable industrial support assistant. Prioritize emergency situations and quickly assess severity. Help customers with parts identification, service scheduling, and technical specifications. Be familiar with industrial equipment terminology and safety protocols.",
    marketContext: "Industrial equipment and services company providing 24/7 support",
    exampleScenarios: [
      "Our pump just failed and production is stopped",
      "I need to find a replacement seal for model XJ-500",
      "Schedule preventive maintenance for next week",
      "What are the specs for the hydraulic system?"
    ]
  },

  caseStudies: [
    {
      companyName: 'Industrial Equipment Solutions',
      companySize: '12 branches nationwide',
      location: 'United States',
      challenge: 'Missing critical after hours emergency calls and struggling to staff 24/7 support across all locations',
      implementation: 'Deployed Help Genie for emergency triage, parts ordering, and service scheduling across all branches',
      timeline: '4 weeks',
      results: [
        {
          metric: 'Emergency Response Time',
          value: '-65%',
          improvement: 'Faster initial response'
        },
        {
          metric: 'After Hours Coverage',
          value: '100%',
          improvement: 'Full 24/7 support'
        },
        {
          metric: 'Parts Order Accuracy',
          value: '+28%',
          improvement: 'Fewer returns'
        },
        {
          metric: 'Customer Retention',
          value: '+15%',
          improvement: 'Year-over-year improvement'
        }
      ],
      quote: {
        text: "In industrial, every minute of downtime matters. Help Genie ensures we never miss an emergency call and our customers get the support they need instantly, day or night.",
        author: 'James Morrison',
        title: 'Director of Customer Service'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Industrial Voice Genie Playbook',
      description: 'Complete guide to implementing voice technology for industrial support',
      url: '/resources/industrial-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Reducing Equipment Downtime with Voice Technology',
      description: 'Learn how industrial companies improve emergency response times',
      url: '/webinars/industrial-support'
    },
    {
      type: 'template',
      title: 'Emergency Triage Scripts',
      description: 'Pre-built voice scripts for industrial emergency situations',
      url: '/templates/industrial-emergency'
    }
  ],

  faqs: [
    {
      question: 'How does the genie handle true emergencies?',
      answer: 'The genie is trained to quickly assess severity through targeted questions. For critical situations, it immediately captures key information and triggers your emergency dispatch protocol while staying on the line with the customer.'
    },
    {
      question: 'Can it help identify parts without a part number?',
      answer: 'Yes, the genie can guide customers through equipment model identification, describe the part visually, and use cross-reference databases to find the correct part number from descriptions or superseded numbers.'
    },
    {
      question: 'Does it integrate with our dispatch system?',
      answer: 'Help Genie integrates with leading field service platforms including ServiceMax, IFS, and Salesforce Field Service to check technician availability and schedule service calls directly.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "Equipment fails at 2 AM\nyour Genie is already on it",
      subtitle: "See how your Genie handles real industrial calls — triaging emergencies, identifying parts, and coordinating service so your customers stay operational.",
    },
    chatScenarios: [
      {
        id: 'equipment_emergency',
        title: 'Equipment Emergency',
        messages: [
          { type: 'user' as const, text: "Our main hydraulic press is leaking fluid badly and we've shut down the line. This is costing us $5,000 an hour.", delay: 300 },
          { type: 'genie' as const, text: "Understood — this is a critical situation. I'm flagging this as an emergency. Can you tell me the press model and where the leak is coming from? Is it from a hose, cylinder, or fitting?", delay: 1200 },
          { type: 'user' as const, text: "It's the Parker 450-ton, looks like it's coming from the main cylinder seal area", delay: 2500 },
          { type: 'genie' as const, text: "Parker 450-ton, main cylinder seal leak. That's likely a rod seal failure. I'm dispatching our emergency service team now. What's your facility name and the best contact number on the floor?", delay: 3400 },
          { type: 'user' as const, text: "Midwest Metal Stamping, plant 2. Call the floor supervisor at 555-891-2345", delay: 4600 },
          { type: 'genie' as const, text: "Emergency ticket created for Midwest Metal Stamping, plant 2. A field technician with seal kit inventory is being dispatched — estimated arrival within 90 minutes. Your floor supervisor will receive a call in 5 minutes with tech details.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Emergency severity confirmed ($5K/hr)', delay: 6500 },
          { id: '2', text: 'Equipment identified (Parker 450-ton)', delay: 6700 },
          { id: '3', text: 'Probable failure mode assessed', delay: 6900 },
          { id: '4', text: 'Emergency tech dispatched (90 min ETA)', delay: 7100 },
          { id: '5', text: 'Floor supervisor notified directly', delay: 7300 },
        ],
      },
      {
        id: 'parts_identification',
        title: 'Parts Identification',
        messages: [
          { type: 'user' as const, text: "I need a replacement seal kit for a pump but I don't have the part number. It's on a Grundfos CR 32-2, installed about 2018.", delay: 300 },
          { type: 'genie' as const, text: "No problem — I can cross-reference that. For the Grundfos CR 32-2, the standard seal kit is part number 96525453. Is this the AQQE or AQQV variant? The shaft seal differs between them.", delay: 1200 },
          { type: 'user' as const, text: "Let me check the nameplate... it says AQQE", delay: 2500 },
          { type: 'genie' as const, text: "AQQE uses the mechanical shaft seal. The complete seal kit for that variant is 96525453. I show 3 in stock. Would you like to order one?", delay: 3400 },
          { type: 'user' as const, text: "Yes, ship it overnight. Account number IND-7723, attention to maintenance dept.", delay: 4600 },
          { type: 'genie' as const, text: "Order placed — seal kit 96525453, overnight shipping, charged to IND-7723, attention maintenance. You'll receive tracking within the hour. Should arrive by 10 AM tomorrow.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Part cross-referenced from equipment model', delay: 6500 },
          { id: '2', text: 'Correct variant identified (AQQE)', delay: 6700 },
          { id: '3', text: 'Stock availability confirmed', delay: 6900 },
          { id: '4', text: 'Order placed with overnight shipping', delay: 7100 },
          { id: '5', text: 'Downtime minimized (next-day delivery)', delay: 7300 },
        ],
      },
      {
        id: 'pm_scheduling',
        title: 'Service Scheduling',
        messages: [
          { type: 'user' as const, text: "We need to schedule the annual preventive maintenance for our three overhead cranes. Ideally during our holiday shutdown December 23–27.", delay: 300 },
          { type: 'genie' as const, text: "Smart timing — shutdown windows are the best for crane PM. I can see our crane technician teams have availability December 23–26. Are all three cranes at the same facility?", delay: 1200 },
          { type: 'user' as const, text: "Yes, all in our main warehouse. Two 10-ton bridges and one 5-ton jib.", delay: 2500 },
          { type: 'genie' as const, text: "Perfect. For two 10-ton bridges and a 5-ton jib, we'll need a two-person team for 3 days. That covers inspection, load testing, lubrication, and wire rope check. I can book December 23–25.", delay: 3400 },
          { type: 'user' as const, text: "Book it. Facility manager is Greg Howard, 555-743-1188", delay: 4600 },
          { type: 'genie' as const, text: "Booked — crane PM December 23-25 at your main warehouse. Greg Howard will receive a confirmation email with the service scope and tech arrival details. We'll send a reminder one week before.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Shutdown window matched to availability', delay: 6500 },
          { id: '2', text: 'Equipment inventory documented', delay: 6700 },
          { id: '3', text: 'Service scope defined', delay: 6900 },
          { id: '4', text: 'Three-day PM scheduled', delay: 7100 },
          { id: '5', text: 'Confirmation and reminder queued', delay: 7300 },
        ],
      },
      {
        id: 'safety_report',
        title: 'Safety Report',
        messages: [
          { type: 'user' as const, text: "I need to report a near-miss incident. One of the forklift batteries started smoking during charging in our battery room.", delay: 300 },
          { type: 'genie' as const, text: "That's a serious near-miss. First — is anyone injured, and has the area been evacuated and ventilated?", delay: 1200 },
          { type: 'user' as const, text: "No injuries. We evacuated the battery room and disconnected the charger. The battery has cooled down now.", delay: 2500 },
          { type: 'genie' as const, text: "Good response. I'm documenting this as a safety near-miss. I need a few details: which charger station number, what brand/model of battery, and approximately what time did this occur?", delay: 3400 },
          { type: 'user' as const, text: "Station 3, it's an EnerSys Hawker battery, happened about 45 minutes ago at 2:15 PM", delay: 4600 },
          { type: 'genie' as const, text: "Near-miss report NM-2024-0147 created. Station 3, EnerSys Hawker battery, 2:15 PM. I've flagged station 3 as out of service and notified your safety manager. The charger and battery should not be used until inspected. Do you want me to schedule an inspection?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Safety status confirmed (no injuries)', delay: 6500 },
          { id: '2', text: 'Incident details captured', delay: 6700 },
          { id: '3', text: 'Near-miss report filed (NM-2024-0147)', delay: 6900 },
          { id: '4', text: 'Equipment locked out of service', delay: 7100 },
          { id: '5', text: 'Safety manager notified', delay: 7300 },
        ],
      },
      {
        id: 'after_hours_breakdown',
        title: 'After Hours',
        messages: [
          { type: 'user' as const, text: "It's 11 PM, third shift. Our air compressor just tripped offline and we can't run the pneumatic lines. Model Atlas Copco GA-75.", delay: 300 },
          { type: 'genie' as const, text: "Compressor down on third shift is critical. For the GA-75, let's check the basics first — can you look at the display panel? Is there an error code or alarm message?", delay: 1200 },
          { type: 'user' as const, text: "It says 'High discharge temperature' and it won't restart", delay: 2500 },
          { type: 'genie' as const, text: "High discharge temp on a GA-75 is often a clogged oil cooler or low oil level. Check the oil sight glass on the side — is the oil level visible?", delay: 3400 },
          { type: 'user' as const, text: "Oil level looks fine actually. The cooler fins look pretty plugged with dust though.", delay: 4600 },
          { type: 'genie' as const, text: "That's your problem. Blow out the cooler fins with shop air — that usually clears it. Wait 15 minutes for the unit to cool, then try restarting. If it trips again, I'll dispatch a tech for morning. I'm logging this for a recommended cooler cleaning service.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Error code diagnosed remotely', delay: 6500 },
          { id: '2', text: 'Guided troubleshooting performed', delay: 6700 },
          { id: '3', text: 'Root cause identified (cooler fins)', delay: 6900 },
          { id: '4', text: 'Resolution attempted on-site', delay: 7100 },
          { id: '5', text: 'Preventive service recommended', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Stop Downtime Before It Starts",
      subtitle: "When a pump fails at midnight or a crane needs parts before Monday, your Genie responds instantly — triaging, identifying, and dispatching so operations keep moving.",
    },
    useCaseCards: [
      {
        id: 'emergency-triage',
        title: 'Emergency Response & Triage',
        description: "Equipment failures cost thousands per hour. Your Genie assesses severity, guides basic troubleshooting, and dispatches techs — in minutes, not hours.",
        bullets: [
          'Severity-based prioritization',
          'Model-specific troubleshooting guidance',
          'Emergency tech dispatch with parts',
          '70% faster initial response time',
        ],
        image: '/solutions-pages/industrial/usecase-emergency-triage.webp',
      },
      {
        id: 'parts-lookup',
        title: 'Parts Identification & Ordering',
        description: "No part number? No problem. Your Genie cross-references by model, description, or superseded number — and places the order immediately.",
        bullets: [
          'Cross-reference by model or description',
          'Real-time stock availability',
          'Overnight and expedited shipping',
          'Account-based pricing applied',
        ],
        image: '/solutions-pages/industrial/usecase-parts-ordering.webp',
      },
      {
        id: 'preventive-maintenance',
        title: 'Preventive Maintenance Scheduling',
        description: "Annual inspections, load testing, calibration — your Genie books service during planned shutdowns and sends reminders so nothing is missed.",
        bullets: [
          'Shutdown-window scheduling',
          'Multi-equipment service coordination',
          'Automated PM reminders',
          'Compliance documentation tracked',
        ],
        image: '/solutions-pages/industrial/usecase-preventive-maintenance.webp',
      },
      {
        id: 'safety-incident',
        title: 'Safety & Incident Reporting',
        description: "Near-misses and safety incidents captured immediately — with structured data, equipment lockout flags, and automatic escalation to safety managers.",
        bullets: [
          '24/7 incident reporting',
          'Structured data capture (OSHA-ready)',
          'Equipment lockout flagging',
          'Safety manager auto-notification',
        ],
        image: '/solutions-pages/industrial/usecase-safety-reporting.webp',
      },
    ],
    finalCTA: {
      title: "Your Operations Never Stop",
      subtitle: "Third shift breakdowns, international parts orders, weekend emergencies — your Genie handles it all with the urgency and expertise your industrial customers demand.",
      pillars: [
        {
          title: 'Always\nResponding',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Emergencies triaged and techs dispatched — at 2 AM or 2 PM.',
        },
        {
          title: 'Always\nSupplying',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Parts identified, stock checked, and orders shipped — overnight when needed.',
        },
        {
          title: 'Always\nProtecting',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Safety incidents captured, equipment locked out, and managers notified — instantly.',
        },
      ],
      backgroundImage: '/solutions-pages/industrial/cta-background.webp',
    },
  }
};
